import { AnyValueMap, Logger, SeverityNumber } from '@opentelemetry/api-logs';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http';
import { BatchLogRecordProcessor, LoggerProvider, LoggerProviderConfig } from '@opentelemetry/sdk-logs';
import { Resource } from '@opentelemetry/resources';
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions';
import Vue from 'vue';
// eslint-disable-next-line import/no-named-as-default
import EnvironmentService from './EnvironmentService';
import { initialSettingsService } from './InitialSettingsService';

class OtlpLoggingService {
  private static serviceInstance: OtlpLoggingService;

  public static get instance() {
    // eslint-disable-next-line no-return-assign
    return this.serviceInstance || (this.serviceInstance = new this());
  }

  private get apiUrl(): string {
    const env = EnvironmentService.getEnv();
    return `https://lpb-events-${env}.uk-tla.com/api/t-event`;
  }

  public setupLogging(
  ): void {
    const collectorOptions = {
      url: this.apiUrl,
      headers: {} // prevents access-control-allow-credentials being required
    };
    try {
      const logExporter = new OTLPLogExporter(collectorOptions);
      const resource = new Resource({
            [ATTR_SERVICE_NAME]: 'form-builder',
            Environment: `client-${EnvironmentService.getEnv()}`
          });
      const loggerProviderConfig = { resource } as LoggerProviderConfig;
      const loggerProvider = new LoggerProvider(loggerProviderConfig);
      
      loggerProvider.addLogRecordProcessor(new BatchLogRecordProcessor(logExporter));
      
      const logger = loggerProvider.getLogger('default', '1.0.0');

      this.overrideLogFunction(logger);
      this.overrideWarnFunction(logger);
      this.overrideErrorFunction(logger);

      window.onerror = (message, url, lineNumber, colNo, error) => {
        logger.emit({
          severityNumber: SeverityNumber.ERROR,
          severityText: 'error',
          body: message as any,
          attributes: this.getAttributes(error)
        });
        return false;
      };
    } catch {
      console.warn('Unable to setup error logging');
    }
  }

  private overrideLogFunction(
    logger: Logger
  ) {
    const consoleLog = console.log;

    console.log = this.getLogFunction(
      logger,
      SeverityNumber.INFO,
      'info',
      consoleLog
    );
  }

  private overrideWarnFunction(
    logger: Logger
  ) {
    const consoleWarn = console.warn;

    console.warn = this.getLogFunction(
      logger,
      SeverityNumber.WARN,
      'warn',
      consoleWarn
    );
  }

  private overrideErrorFunction(
    logger: Logger
  ) {
    const consoleErr = console.error;

    console.error = this.getLogFunction(
      logger,
      SeverityNumber.ERROR,
      'error',
      consoleErr
    );
  }

  private getLogFunction(
    logger : Logger,
    severityNumber : SeverityNumber,
    severityText : string,
    existingLogFn : (message? : any, ...data : any[]) => void
  ) : (message? : any, ...data : any[]) => void {
    return (message? : any, ...data : any[]) => {
      try {
        const attributes = this.getAttributes(message);

        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < data.length; i++) {
          attributes[`arg${i}`] = data[i];
        }
        if (message instanceof Error) {
          logger.emit({
            severityNumber,
            severityText,
            body: message.message as any,
            attributes
          });
        } else {
          // Emit a log
          logger.emit({
            severityNumber,
            severityText,
            body: message,
            attributes
          });
        }
      } finally {
        // don't print empty array
        if (data.length) {
          existingLogFn(message, ...data);
        } else {
          existingLogFn(message);
        }
      }
    };
  }

  private getAttributes(message : any) : AnyValueMap {
    const attributes = {
      url: window.location.href,
      userAgent: navigator.userAgent,
      'TLA.CorrelationId': initialSettingsService.getOrGenerateCorrelationId()
    };

    if (message instanceof Error) {
      attributes['exception.message'] = message.message as any;
      attributes['exception.type'] = message.name as any;
      attributes['exception.stacktrace'] = message.stack as any;
    }

    return attributes;
  }
}

// export a singleton instance in the global namespace
// eslint-disable-next-line import/prefer-default-export
export const otlpLoggingService = OtlpLoggingService.instance;
